<!--
 * @FileDescription: 信用用户
 * @Author: 陈文景
 * @Date: 2022-12-28 10:33:12
 * @LastEditors: 最后更新作者
 * @LastEditTime: 最后更新时间
-->
<template>
  <PageSkeleton>
    <template slot="header-left">
      <el-button size="medium" type="primary" @click="handleAddCredits">新增</el-button>

      <el-select v-model="parameter.userGroup" multiple collapse-tags filterable clearable size="medium" placeholder="请选择用户类型" @change="getCreditsUserList">
        <el-option v-for="item in userTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select v-model="parameter.billType" multiple collapse-tags filterable clearable size="medium" placeholder="请选择生成类型" @change="getCreditsUserList">
        <el-option v-for="item in generateTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select v-model="parameter.billCycle" multiple collapse-tags filterable clearable size="medium" placeholder="请选择账期周期" @change="getCreditsUserList">
        <el-option v-for="item in paymentDaysOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <BatchSearch @clear="handleClearBatchSearch" @search="handleSearchBatchSearch" />
    </template>

    <template slot="container">
      <el-table v-loading="loading" :data="credits" size="medium" row-key="id" :style="{ width: `${tableWidth}%` }" :height="`${tableHeight}%`">
        <el-table-column prop="userId" label="用户名称" show-overflow-tooltip />
        <el-table-column prop="showName" label="邮箱/手机号" show-overflow-tooltip />
        <el-table-column prop="creditLimit" label="信用额度(USD)" show-overflow-tooltip>
          <template v-slot="{ row }">
            {{ creditLimit(row.creditLimit) }}
          </template>
        </el-table-column>
        <el-table-column label="可用额度(USD)" show-overflow-tooltip>
          <template v-slot="{ row }">
            {{ usableQuota(row.creditLimit, row.usedLimit) }}
          </template>
        </el-table-column>
        <el-table-column show-overflow-tooltip>
          <template slot="header">
            <span>生成类型</span>
            <el-tooltip effect="dark" placement="top">
              <div slot="content">
                按付款时间生成：订单付款后自动生成未出账账单
                <br />
                按发货时间生成：包裹状态变为已发货后自动生成未出账账单
              </div>
              <i class="icon-question el-icon-question" />
            </el-tooltip>
          </template>
          <template v-slot="{ row }">
            {{ billType(row.billType) }}
          </template>
        </el-table-column>
        <el-table-column label="账期周期" show-overflow-tooltip>
          <template v-slot="{ row }">
            {{ billCycle(row.billCycle) }}
          </template>
        </el-table-column>
        <el-table-column prop="userGroup" label="用户类型" show-overflow-tooltip>
          <template v-slot="{ row }">
            {{ userGroup(row.userGroup) }}
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="操作" show-overflow-tooltip align="center" width="120">
          <template v-slot="{ row }">
            <el-button type="text" size="medium" @click="() => handleEditCredits(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <template #footer-right>
      <el-pagination background :current-page="parameter.pageNumber" :page-sizes="[20, 50, 100, 200, 500]" :page-size="parameter.pageSize" layout="total, sizes, prev, pager, next, jumper" :total="total" class="el-pagination" @size-change="sizeChange" @current-change="currentChange" />
    </template>

    <!-- 新增信用用户对话框 -->
    <AddCreditUserDialog ref="addCreditUserDialog" @update="getCreditsUserList" />

    <!-- 设置信用额度对话框 -->
    <SetCreditQuotaDialog ref="setCreditQuotaDialog" @update="getCreditsUserList" />
  </PageSkeleton>
</template>

<script>
import API from '@/api'
import { formatTimeStamp } from '@/utils/moment'
import BatchSearch from './components/BatchSearch.vue'
import PageSkeleton from '@/components/PageSkeleton.vue'
import AddCreditUserDialog from './components/AddCreditUserDialog.vue'
import SetCreditQuotaDialog from './components/SetCreditQuotaDialog.vue'

let parameter = {
  userGroup: [], // 用户类型
  billType: [], // 生成类型
  billCycle: [], // 账期周期
  searchContent: undefined, // 搜索的内容
  pageSize: 20,
  pageNumber: 1
}

export default {
  name: 'finance_credit',
  components: {
    BatchSearch,
    PageSkeleton,
    AddCreditUserDialog,
    SetCreditQuotaDialog
  },
  data() {
    return {
      tableWidth: 100,
      tableHeight: 100,

      formatTimeStamp,

      // 用户类型下拉选项
      userTypeOptions: [
        { label: '独立站', value: 100 },
        { label: '默认', value: -1 }
      ],

      // 生成类型下拉选项
      generateTypeOptions: [
        { label: '按付款时间', value: 1 },
        { label: '按发货时间', value: 2 }
      ],

      // 账单周期下拉选项
      paymentDaysOptions: [
        { label: '15天', value: 15 },
        { label: '30天', value: 30 },
        { label: '45天', value: 45 },
        { label: '60天', value: 60 },
        { label: '90天', value: 90 },
        { label: '120天', value: 120 }
      ],

      parameter: { ...parameter },
      credits: [],
      total: 0,
      loading: false // 表格loading
    }
  },
  computed: {
    creditLimit() {
      return (value) => {
        return value ? `+US$${value}` : '-'
      }
    },
    paidAmount() {
      return (value) => {
        return value ? `+US$${value}` : '-'
      }
    },
    usableQuota() {
      return (creditLimit, usedLimit) => {
        return creditLimit && (usedLimit || usedLimit === 0) ? (creditLimit - usedLimit < 0 ? 0 : `+US$${(creditLimit - usedLimit).toFixed(2)}`) : '-'
      }
    },
    billType() {
      return (value) => {
        switch (value) {
          case 1:
            return '按付款时间'
          case 2:
            return '按发货时间'
          default:
            return '-'
        }
      }
    },
    billCycle() {
      return (value) => {
        switch (value) {
          case 15:
            return '15天'
          case 30:
            return '30天'
          case 45:
            return '45天'
          case 60:
            return '60天'
          case 90:
            return '90天'
          case 120:
            return '120天'
          default:
            return '-'
        }
      }
    },
    userGroup() {
      return (value) => {
        switch (value) {
          case -1:
            return '默认'
          case 100:
            return '独立站'
          default:
            return '-'
        }
      }
    }
  },
  created() {
    this.getCreditsUserList()
  },
  methods: {
    /**
     * API 查询信用用户列表
     *
     * @param {Boolean} reset 是否重置分页
     */
    async getCreditsUserList(reset = true) {
      try {
        if (reset) {
          this.parameter.pageNumber = 1
        }
        this.loading = true
        let { data } = await API.getCreditsUserList(this.parameter)
        this.total = data.total
        this.credits = data.list
        this.loading = false
      } catch (error) {
        this.loading = false
      }
    },

    /**
     * 新增
     */
    handleAddCredits() {
      this.$refs.addCreditUserDialog.show()
    },

    /**
     * 批量搜索清除内容的回调事件
     */
    handleClearBatchSearch() {
      this.parameter.searchContent = undefined
      this.getCreditsUserList()
    },

    /**
     * 批量搜索的回调事件
     *
     * @param {String} value 搜索关键字
     */
    handleSearchBatchSearch(value) {
      if (value) {
        this.parameter.searchContent = value.trim()
      }
      this.getCreditsUserList()
    },

    /**
     * 编辑
     *
     * @param {Object} row 当前点击的行数据
     */
    handleEditCredits(row) {
      this.$refs.setCreditQuotaDialog.show(row)
    },

    /**
     * 每页显示条数发生改变时的回调
     * @param {Number} size 每页显示条数
     */
    sizeChange(size) {
      this.parameter.pageSize = size
      this.getCreditsUserList(false)
    },

    /**
     * 上一页/下一页/当前页发生改变时的回调
     * @param {Number} current 当前页
     */
    currentChange(current) {
      this.parameter.pageNumber = current
      this.getCreditsUserList(false)
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-question {
  margin-left: 5px;
  cursor: pointer;
}
</style>
